import React, { useEffect, useRef } from 'react'
import { useSubscribeAnalyticsEvent } from '@pff-consumer/analytics/hooks/useSubscribeAnalyticsEvent'
import { checkoutHooks } from '@pff-consumer/data-provider/index.checkout'
import { SubscriptionPricingState, isMonthlyPlan } from '@pff-consumer/utils'
import { SubscriptionPlans } from '@pff-consumer/schema'
import { Text, TextDescriptionVariant } from '@pff-consumer/tweed-ui/atoms/browser-text'

import { SubscriptionPlanTile } from '../subscription-plan-tile/subscription-plan-tile.web'
import { useSubscriptionPricing } from '../../hooks/subscribe/useSubscriptionPricing'
import { usePaymentMethodState } from '../../hooks/subscribe/usePaymentMethodState'
import { useStyles } from './subscription-layout.styles'
import { PromoCode } from '../promo-code/promo-code.web'
import { OneStepCheckout } from '../one-step-checkout/one-step-checkout.web'
import { ScreenOverlay } from '../screen-overlay/screen-overlay.web'

const { useCheckoutActions, useSelectedPlan, useGetIsLoggedIn, useMonthlyPlan, useGetIsAccountsPage } = checkoutHooks

function monthlyDescription(monthlyPlanPrice: SubscriptionPricingState) {
  const { symbol } = monthlyPlanPrice.currency
  const { subtotal: nowTotal, setup_fee: setupFee } = monthlyPlanPrice.now
  const { subtotal: nextTotal } = monthlyPlanPrice.next

  // plans with setup fees / season pass fee
  if (setupFee !== '0.00') {
    return <span>One time draft kit fee billed only at sign up </span>
  }
  // single-use coupons
  if (nowTotal !== nextTotal) {
    return <span>{`Billed ${symbol}${nowTotal} for first month`}</span>
  }
  // forever coupons
  return <span>{`Only ${symbol}${nowTotal} billed monthly`}</span>
}

function annualDescription(annualPlanPrice: SubscriptionPricingState) {
  const { symbol } = annualPlanPrice.currency
  const { total: nowTotal } = annualPlanPrice.now
  const { total: nextTotal } = annualPlanPrice.next

  // single-use coupons
  if (nowTotal !== nextTotal) {
    return <span>{`Billed ${symbol}${nowTotal} for first year`}</span>
  }
  // forever coupons
  return <span>{`Billed annually at ${symbol}${nowTotal}`}</span>
}

export const SubscriptionLayout = () => {
  const headerRef = useRef<HTMLHeadingElement>(null)
  const isAccountsPage = useGetIsAccountsPage()
  const { updateSelectedPlan, updatePromoCode } = useCheckoutActions()
  const plan = useSelectedPlan()
  const isAnnualPlanSelected = plan === SubscriptionPlans.PREMIUM_ANNUAL
  const isMonthlyPlanSelected = isMonthlyPlan(plan)
  const styles = useStyles()
  const isLoggedIn = useGetIsLoggedIn()
  const { sendSubscribeAnalyticsEvent } = useSubscribeAnalyticsEvent()
  const monthlyPlan = useMonthlyPlan()
  const { annualPlanPrice, monthlyPlanPrice, isError, isDiscountApplied } = useSubscriptionPricing(monthlyPlan)

  const {
    onComplete,
    onError,
    onDisabledClick,
    onRecurlyTokenInit,
    isEmailValid,
    isOnCompletePending,
    isPayPalProcessing,
    isCreditCardProcessing,
    isApplePayProcessing,
  } = usePaymentMethodState()

  const scrollToTopOfTheContent = () => {
    headerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
  const annualPlanTileStyles = `${styles.planTile} ${isAnnualPlanSelected ? styles.planTileActive : ''}`
  const monthlyPlanTileStyles = `${styles.planTile} ${isMonthlyPlanSelected ? styles.planTileActive : ''}`

  // Track the default selected plan the user receives
  useEffect(() => {
    if (sendSubscribeAnalyticsEvent) {
      const planName = plan.startsWith('premium-monthly') ? 'Monthly' : 'Annual'
      sendSubscribeAnalyticsEvent(`SPC Default ${planName} Plan`)
    }
  }, [sendSubscribeAnalyticsEvent]) // Note: We are intentionally not rendering this on plan changes

  useEffect(() => {
    sendSubscribeAnalyticsEvent('SPC Monthly Plan Variant', {
      variant: monthlyPlan.toString(),
    })
  }, [sendSubscribeAnalyticsEvent, monthlyPlan])

  // This will ensure if the URL has MDS coupon, it will automatically be applied
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    updatePromoCode(urlParams.get('promoCode') || '')
  }, [])

  return (
    <div className={styles.container}>
      <div
        className={styles.header}
        ref={headerRef}
      >
        <Text
          accessibilityRole='header'
          variant={TextDescriptionVariant.LARGE_PRIMARY_BOLD}
        >
          Choose Subscription
        </Text>
      </div>
      <div className={styles.subscriptionPlanContainer}>
        <SubscriptionPlanTile
          type={monthlyPlan}
          currency={monthlyPlanPrice.currency.symbol}
          price={
            monthlyPlan === SubscriptionPlans.PREMIUM_MONTHLY_SEASON
              ? `${monthlyPlanPrice.now.total}/mo`
              : `${monthlyPlanPrice.next.total}/mo + $${parseFloat(monthlyPlanPrice.now.setup_fee).toFixed(
                  0
                )} draft kit fee`
          }
          originalPrice={`${monthlyPlanPrice.next.plan}/mo`}
          hasDiscountedPrice={isDiscountApplied && monthlyPlanPrice.now.total !== monthlyPlanPrice.now.plan}
          description={monthlyDescription(monthlyPlanPrice)}
          description2='Month-to-month commitment'
          plan={plan}
          planStyle={monthlyPlanTileStyles}
          handlePlanClick={updateSelectedPlan}
        />
        <SubscriptionPlanTile
          type={SubscriptionPlans.PREMIUM_ANNUAL}
          currency={annualPlanPrice.currency.symbol}
          price={`${(parseFloat(annualPlanPrice.now.total) / 12).toFixed(3).slice(0, -1)}/mo`}
          originalPrice={`${(parseFloat(annualPlanPrice.next.total) / 12).toFixed(2)}/mo`}
          hasDiscountedPrice={isDiscountApplied && annualPlanPrice.next.total !== annualPlanPrice.next.plan}
          description={annualDescription(annualPlanPrice)}
          description2='Year-to-year commitment'
          plan={plan}
          planStyle={annualPlanTileStyles}
          handlePlanClick={updateSelectedPlan}
        />
      </div>
      <PromoCode
        isCouponValid={!isError}
        isDiscountApplied={isDiscountApplied}
      />
      <OneStepCheckout
        plan={plan}
        price={plan === SubscriptionPlans.PREMIUM_ANNUAL ? annualPlanPrice : monthlyPlanPrice}
        isLoggedIn={isLoggedIn}
        isDiscountApplied={isDiscountApplied}
        onComplete={onComplete}
        onError={onError}
        onDisabledClick={onDisabledClick}
        onRecurlyTokenInit={onRecurlyTokenInit}
        scrollToTopOfTheContent={scrollToTopOfTheContent}
        isEmailValid={isEmailValid}
        isPayPalProcessing={isPayPalProcessing}
        isCreditCardProcessing={isCreditCardProcessing}
        isApplePayProcessing={isApplePayProcessing}
        isOnCompletePending={isOnCompletePending}
        showKitSummaryRow={monthlyPlan === SubscriptionPlans.PREMIUM_MONTHLY_SEASON_PASS}
      />
      {/* This ScreenOverlay not needed for accounts as CheckoutErrorModal is not used */}
      {!isAccountsPage && isOnCompletePending && <ScreenOverlay />}
    </div>
  )
}
